import React, { useState, useEffect } from 'react';
import StartRemoteDebugTab from './StartRemoteDebug';
import ScheduledSessionsTab from './ScheduledSessionsTab';
import EndRemoteDebugTab from './EndRemoteDebug';
import ViewRemoteDebugSessionTab from './ViewRemoteDebugSession';
import '../assets/RemoteDebugSessionTabs.css';

/**
 * Device Diagnostics — second-level grouping tab.
 *
 * Owns the diagnostics session sub-tabs (Start / Scheduled / Active / Completed)
 * that previously lived directly under Remote Diagnostics. Behavior of the four
 * child screens is unchanged; this container only supplies the third-level
 * sub-tab strip and reproduces the deep-link prefill behavior.
 *
 * Deep-link: DeviceDetailsTab emits prefilledSystemId = { systemId, subTab }
 * where subTab is 'Start' or 'Active'. We select that sub-tab and pass the
 * systemId down (same contract the old RemoteDebugSessionTabs had).
 */
function DeviceDiagnostics({ userInfo, role, prefilledSystemId, setPrefilledSystemId, userGroups = [] }) {
  const subTabs = [
    { key: 'Start', label: 'Start' },
    { key: 'Scheduled', label: 'Scheduled' },
    { key: 'Active', label: 'Active' },
    { key: 'Completed', label: 'Completed' },
  ];

  const [activeSubTab, setActiveSubTab] = useState('Start');
  const [localPrefilledData, setLocalPrefilledData] = useState(null);

  useEffect(() => {
    if (prefilledSystemId && typeof prefilledSystemId === 'object' && prefilledSystemId.subTab) {
      setActiveSubTab(prefilledSystemId.subTab);
      setLocalPrefilledData(prefilledSystemId);
    } else if (prefilledSystemId) {
      // Fallback for a bare string prefilledSystemId.
      setActiveSubTab('Start');
      setLocalPrefilledData({ systemId: prefilledSystemId, subTab: 'Start' });
    } else {
      setLocalPrefilledData(null);
    }
  }, [prefilledSystemId]);

  const renderSubTabContent = () => {
    const systemIdToPass = localPrefilledData ? localPrefilledData.systemId : null;

    switch (activeSubTab) {
      case 'Start':
        return <StartRemoteDebugTab userInfo={userInfo} prefilledSystemId={systemIdToPass} setPrefilledSystemId={setPrefilledSystemId} />;
      case 'Scheduled':
        return <ScheduledSessionsTab userInfo={userInfo} role={role} prefilledSystemId={systemIdToPass} />;
      case 'Active':
        return <EndRemoteDebugTab userInfo={userInfo} role={role} prefilledSystemId={systemIdToPass} userGroups={userGroups} />;
      case 'Completed':
        return <ViewRemoteDebugSessionTab userInfo={userInfo} role={role} userGroups={userGroups} />;
      default:
        return <StartRemoteDebugTab userInfo={userInfo} prefilledSystemId={systemIdToPass} setPrefilledSystemId={setPrefilledSystemId} />;
    }
  };

  return (
    <div className="remote-debug-session-container">
      <div className="sub-tab-content">
        <div className="sub-tabs-container nested">
          {subTabs.map((tab) => (
            <div
              key={tab.key}
              className={`sub-tab ${activeSubTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveSubTab(tab.key)}
            >
              {tab.label}
            </div>
          ))}
        </div>
        {renderSubTabContent()}
      </div>
    </div>
  );
}

export default DeviceDiagnostics;
